// The vehicle card (demo case 2): everything the register knows, what the organisation knows,
// free-text fields, mileage, costs, service history, bookings and history, in one view with
// edit in place for the administrator.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { VehicleCard } from '@aldorock-fordon/shared/api-types';
import { api, ApiError, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Facts, type Fact } from '../ui/Facts.tsx';
import { REGISTER_HEADINGS, REGISTER_SUMMARY } from '@aldorock-fordon/shared/register-headings';
import { Table } from '../ui/Table.tsx';
import { SelectField, TextArea, TextField, ErrorSummary } from '../ui/Fields.tsx';
import { BookingStatusView, VehicleStatusView } from '../ui/Status.tsx';
import { day, km, kr, moment, num, plate, span, vehicleName } from '../format.ts';
import {
  ECONOMY_FIELDS,
  ORG_FIELDS,
  PLACE_FIELDS,
  REGISTER_FIELDS,
  display,
  fromDraft,
  toDraft,
  type Draft,
  type FieldDef,
} from './vehicleForm.ts';
import { CostForm, OdometerForm, ServiceForm, WorkshopSection } from './VehicleEntry.tsx';
import { DocumentsSection } from './VehicleDocuments.tsx';

type Unit = { id: string; name: string; kind: string; path: string; parentId: string | null };

function facts(defs: FieldDef[], v: VehicleCard['vehicle']): Fact[] {
  return defs.map((f) => ({
    key: f.key,
    label: sv.vehicleCard.fields[f.key],
    value: display(f, v[f.key as keyof typeof v]) || sv.none,
  }));
}

export function FieldInputs({
  defs,
  draft,
  setDraft,
  errors,
  units,
}: {
  defs: FieldDef[];
  draft: Draft;
  setDraft: (d: Draft) => void;
  errors: Record<string, string>;
  units?: Unit[];
}) {
  return (
    <>
      {defs.map((f) =>
        f.kind === 'select' && f.options ? (
          <SelectField
            key={f.key}
            id={f.key}
            label={sv.vehicleCard.fields[f.key]}
            value={draft[f.key] ?? ''}
            options={Object.entries(f.options).map(([value, label]) => ({ value, label }))}
            error={errors[f.key]}
            onChange={(v) => setDraft({ ...draft, [f.key]: v })}
          />
        ) : (
          <TextField
            key={f.key}
            id={f.key}
            label={sv.vehicleCard.fields[f.key]}
            value={draft[f.key] ?? ''}
            type={f.kind === 'int' ? 'number' : f.kind === 'date' ? 'date' : 'text'}
            error={errors[f.key]}
            onChange={(v) => setDraft({ ...draft, [f.key]: v })}
          />
        ),
      )}
      {units ? (
        <SelectField
          id="orgUnitId"
          label={sv.vehicleCard.fields.orgUnitId}
          value={draft['orgUnitId'] ?? ''}
          options={units
            .filter((u) => u.kind !== 'organisation')
            .map((u) => ({
              value: u.id,
              label:
                u.kind === 'forvaltning'
                  ? u.name
                  : `${u.name} (${units.find((p) => p.id === u.parentId)?.name ?? ''})`,
            }))}
          error={errors['orgUnitId']}
          onChange={(v) => setDraft({ ...draft, orgUnitId: v })}
        />
      ) : null}
    </>
  );
}

export function VehiclePage({ me }: { me: Me }) {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api<VehicleCard>(`/vehicles/${id}`),
  });
  const units = useQuery({
    queryKey: ['units'],
    queryFn: () => api<Unit[]>('/vehicles/units'),
    enabled: me.permissions['vehicle.write'],
  });
  const d = q.data;
  const title = d
    ? sv.vehicleCard.title(
        plate(d.vehicle.registrationNumber),
        [d.vehicle.make, d.vehicle.model].filter(Boolean).join(' '),
      )
    : sv.app.loading;
  const h1 = usePage(title);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const canWrite = me.permissions['vehicle.write'];
  const [entry, setEntry] = useState<'cost' | 'service' | 'odometer' | null>(null);

  const startEdit = () => {
    if (!d) return;
    setDraft({ ...toDraft(d.vehicle), orgUnitId: d.vehicle.orgUnitId ?? '' });
    setCustom(Object.fromEntries(d.customValues.map((c) => [c.fieldId, c.value])));
    setErrors({});
    setMessage(null);
    setEditing(true);
  };
  const save = useMutation({
    mutationFn: () =>
      api(`/vehicles/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...fromDraft(draft),
          orgUnitId: draft['orgUnitId'] || null,
          customValues: Object.entries(custom).map(([fieldId, value]) => ({ fieldId, value })),
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vehicle', id] });
      setEditing(false);
      setMessage(sv.vehicleCard.saved);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length)
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
      else setMessage(sv.errors.server);
    },
  });
  const refresh = useMutation({
    mutationFn: () => api<{ source: string }>(`/vehicles/${id}/refresh`, { method: 'POST' }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['vehicle', id] });
      setMessage(sv.vehicleCard.refreshed(sv.sources[r.source] ?? r.source));
    },
    onError: () => setMessage(sv.vehicleCard.refreshFailed),
  });

  return (
    <>
      <PageHead
        help="vehicle"
        crumbs={[
          { to: '/fordon', label: sv.vehicleCard.crumb },
          { label: d ? plate(d.vehicle.registrationNumber) : '' },
        ]}
        title={title}
        titleRef={h1}
        status={d ? <VehicleStatusView status={d.vehicle.status} /> : null}
        actions={
          d && canWrite ? (
            editing ? null : (
              <>
                <button
                  className="btn"
                  type="button"
                  disabled={refresh.isPending}
                  aria-busy={refresh.isPending}
                  onClick={() => refresh.mutate()}
                >
                  {sv.vehicleCard.refresh}
                </button>
                <button className="btn btn-primary" type="button" onClick={startEdit}>
                  {sv.vehicleCard.edit}
                </button>
              </>
            )
          ) : null
        }
      />
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {d && editing ? (
        <form
          className="form-block wide"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <ErrorSummary errors={Object.entries(errors).map(([k, m]) => ({ id: k, message: m }))} />
          <h2 className="form-heading">{sv.vehicleCard.sections.register}</h2>
          <FieldInputs defs={REGISTER_FIELDS} draft={draft} setDraft={setDraft} errors={errors} />
          <h2 className="form-heading">{sv.vehicleCard.sections.organisation}</h2>
          <FieldInputs
            defs={ORG_FIELDS}
            draft={draft}
            setDraft={setDraft}
            errors={errors}
            units={units.data}
          />
          <h2 className="form-heading">{sv.vehicleCard.sections.placement}</h2>
          <FieldInputs defs={PLACE_FIELDS} draft={draft} setDraft={setDraft} errors={errors} />
          <h2 className="form-heading">{sv.vehicleCard.sections.economy}</h2>
          <FieldInputs defs={ECONOMY_FIELDS} draft={draft} setDraft={setDraft} errors={errors} />
          <h2 className="form-heading">{sv.vehicleCard.sections.freeText}</h2>
          {d.customValues.map((c) => (
            <TextArea
              key={c.fieldId}
              id={`custom-${c.key}`}
              label={c.label}
              value={custom[c.fieldId] ?? ''}
              rows={3}
              onChange={(v) => setCustom({ ...custom, [c.fieldId]: v })}
            />
          ))}
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={save.isPending}
              aria-busy={save.isPending}
            >
              {sv.vehicleCard.save}
            </button>
            <button className="btn" type="button" onClick={() => setEditing(false)}>
              {sv.vehicleCard.cancelEdit}
            </button>
          </div>
        </form>
      ) : null}
      {d && !editing ? (
        <>
          <div className="cols cols-2">
            <section className="block" aria-labelledby="s-register">
              <h2 className="block-title" id="s-register">
                {sv.vehicleCard.sections.register}
              </h2>
              <Facts
                top={false}
                facts={facts(
                  REGISTER_FIELDS.filter((f) => f.key !== 'registrationNumber'),
                  d.vehicle,
                )}
              />
              <p className="caption">
                {d.vehicle.registerSource
                  ? `${sv.vehicleCard.fields.registerSource}: ${sv.sources[d.vehicle.registerSource] ?? d.vehicle.registerSource}, ${sv.vehicleCard.fields.registerFetchedAt.toLowerCase()} ${d.vehicle.registerFetchedAt ? moment(d.vehicle.registerFetchedAt) : ''}`
                  : sv.vehicleCard.noRegister}
              </p>
            </section>
            <div>
              <section className="block" aria-labelledby="s-org">
                <h2 className="block-title" id="s-org">
                  {sv.vehicleCard.sections.organisation}
                </h2>
                <Facts
                  top={false}
                  facts={[
                    {
                      key: 'forv',
                      label: sv.columns.forvaltning,
                      value: d.forvaltning?.name ?? sv.none,
                    },
                    {
                      key: 'unit',
                      label: sv.columns.unit,
                      value: d.unit && d.unit.kind !== 'forvaltning' ? d.unit.name : sv.none,
                    },
                    ...facts(ORG_FIELDS, d.vehicle),
                  ]}
                />
              </section>
              <section className="block" aria-labelledby="s-place">
                <h2 className="block-title" id="s-place">
                  {sv.vehicleCard.sections.placement}
                </h2>
                <Facts top={false} facts={facts(PLACE_FIELDS, d.vehicle)} />
              </section>
              <section className="block" aria-labelledby="s-pool">
                <h2 className="block-title" id="s-pool">
                  {sv.vehicleCard.sections.pool}
                </h2>
                {d.pool ? (
                  <Facts
                    top={false}
                    facts={[
                      {
                        key: 'pool',
                        label: sv.columns.pool,
                        value: sv.vehicleCard.poolLine(d.pool.name, d.pool.priority),
                      },
                      { key: 'note', label: sv.columns.note, value: d.pool.note ?? sv.none },
                      d.keySlot
                        ? {
                            key: 'slot',
                            label: sv.columns.key,
                            value: `${sv.vehicleCard.slotLine(d.keySlot.cabinetName, d.keySlot.slotNumber)}. ${d.keySlot.keyPresent ? sv.vehicleCard.keyIn : sv.vehicleCard.keyOut}`,
                          }
                        : null,
                    ]}
                  />
                ) : (
                  <p className="muted">{sv.vehicleCard.notInPool}</p>
                )}
              </section>
            </div>
          </div>
          <div className="cols cols-2">
            <section className="block" aria-labelledby="s-economy">
              <h2 className="block-title" id="s-economy">
                {sv.vehicleCard.sections.economy}
              </h2>
              <Facts top={false} facts={facts(ECONOMY_FIELDS, d.vehicle)} />
            </section>
            <section className="block" aria-labelledby="s-free">
              <h2 className="block-title" id="s-free">
                {sv.vehicleCard.sections.freeText}
              </h2>
              <Facts
                top={false}
                facts={d.customValues.map((c) => ({
                  key: c.key,
                  label: c.label,
                  value: c.value || sv.none,
                  testId: `custom-${c.key}`,
                }))}
              />
            </section>
          </div>
          {canWrite ? (
            <div className="actions-inline entry-actions">
              <button className="btn" type="button" onClick={() => setEntry('cost')}>
                {sv.entry.addCost}
              </button>
              <button className="btn" type="button" onClick={() => setEntry('service')}>
                {sv.entry.addService}
              </button>
              <button className="btn" type="button" onClick={() => setEntry('odometer')}>
                {sv.entry.addOdometer}
              </button>
            </div>
          ) : null}
          {entry === 'cost' ? <CostForm vehicleId={id!} onDone={() => setEntry(null)} /> : null}
          {entry === 'service' ? (
            <ServiceForm vehicleId={id!} onDone={() => setEntry(null)} />
          ) : null}
          {entry === 'odometer' ? (
            <OdometerForm vehicleId={id!} onDone={() => setEntry(null)} />
          ) : null}
          <div className="cols cols-2">
            <Table
              caption={sv.vehicleCard.sections.odometer}
              isEmpty={d.odometer.length === 0}
              empty={sv.vehicleCard.odometerEmpty}
            >
              <thead>
                <tr>
                  <th scope="col">{sv.columns.date}</th>
                  <th scope="col" className="r">
                    {sv.columns.odometer}
                  </th>
                  <th scope="col">{sv.columns.source}</th>
                </tr>
              </thead>
              <tbody>
                {d.odometer.slice(0, 13).map((o) => (
                  <tr key={o.id}>
                    <td className="num">{day(o.readAt)}</td>
                    <td className="r">{km(o.km)}</td>
                    <td>{sv.sources[o.source] ?? o.source}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <section className="block" aria-labelledby="s-costs">
              <h2 className="block-title" id="s-costs">
                {sv.vehicleCard.sections.costs}
              </h2>
              <Facts
                top={false}
                facts={[
                  {
                    key: 'last12',
                    label: sv.vehicleCard.last12,
                    value: kr(d.costTotals.last12Months),
                    testId: 'cost-12',
                  },
                  {
                    key: 'permil',
                    label: sv.vehicleCard.perMil,
                    value:
                      d.costTotals.perMil !== null
                        ? kr(d.costTotals.perMil)
                        : sv.vehicleCard.perMilNone,
                    testId: 'cost-per-mil',
                  },
                  ...Object.entries(d.costTotals.byCategory)
                    .sort((a, b) => b[1] - a[1])
                    .map(([c, v]) => ({
                      key: c,
                      label: sv.costCategories[c as keyof typeof sv.costCategories] ?? c,
                      value: kr(v),
                    })),
                ]}
              />
              <Table
                caption={sv.columns.cost}
                isEmpty={d.costs.length === 0}
                empty={sv.vehicleCard.costsEmpty}
                hasOptional
              >
                <thead>
                  <tr>
                    <th scope="col">{sv.columns.date}</th>
                    <th scope="col">{sv.columns.category}</th>
                    <th scope="col" className="col-opt">
                      {sv.columns.supplier}
                    </th>
                    <th scope="col" className="r">
                      {sv.columns.amount}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.costs.slice(0, 24).map((c) => (
                    <tr key={c.id}>
                      <td className="num">{c.costDate}</td>
                      <td>{sv.costCategories[c.category]}</td>
                      <td className="col-opt">{c.supplier ?? ''}</td>
                      <td className="r">{kr(c.amountSek)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          </div>
          <Table
            caption={sv.vehicleCard.sections.service}
            isEmpty={d.service.length === 0}
            empty={sv.vehicleCard.serviceEmpty}
            hasOptional
          >
            <thead>
              <tr>
                <th scope="col">{sv.columns.date}</th>
                <th scope="col">{sv.columns.kind}</th>
                <th scope="col" className="r col-opt">
                  {sv.columns.odometer}
                </th>
                <th scope="col" className="col-opt">
                  {sv.columns.workshop}
                </th>
                <th scope="col">{sv.columns.description}</th>
                <th scope="col" className="r col-opt">
                  {sv.columns.cost}
                </th>
                <th scope="col" className="col-opt">
                  {sv.columns.nextDue}
                </th>
              </tr>
            </thead>
            <tbody>
              {d.service.map((s) => (
                <tr key={s.id}>
                  <td className="num">{s.doneOn}</td>
                  <td>{sv.serviceKinds[s.kind]}</td>
                  <td className="r col-opt">{s.odometerKm != null ? num(s.odometerKm) : ''}</td>
                  <td className="col-opt">{s.workshop ?? ''}</td>
                  <td>{s.description ?? ''}</td>
                  <td className="r col-opt">{s.costSek != null ? kr(s.costSek) : ''}</td>
                  <td className="col-opt num">{s.nextDueOn ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <WorkshopSection vehicleId={id!} canWrite={me.permissions['workshop.write']} />
          <DocumentsSection vehicleId={id!} canWrite={canWrite} />
          <div className="cols cols-2">
            <Table
              caption={sv.vehicleCard.sections.bookings}
              isEmpty={d.bookings.length === 0}
              empty={sv.vehicleCard.bookingsEmpty}
            >
              <thead>
                <tr>
                  <th scope="col">{sv.columns.time}</th>
                  <th scope="col">{sv.columns.driver}</th>
                  <th scope="col">{sv.columns.status}</th>
                </tr>
              </thead>
              <tbody>
                {d.bookings.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <Link to={`/bokning/${b.id}`}>{span(b.startsAt, b.endsAt)}</Link>
                    </td>
                    <td>{b.driver}</td>
                    <td>
                      <BookingStatusView status={b.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <section className="block" aria-labelledby="s-history">
              <h2 className="block-title" id="s-history">
                {sv.vehicleCard.sections.history}
              </h2>
              {d.events.length === 0 ? (
                <p className="muted">{sv.vehicleCard.historyEmpty}</p>
              ) : null}
              <ul className="history">
                {d.events.map((e) => (
                  <li key={e.id}>
                    <time dateTime={e.occurredAt}>{moment(e.occurredAt)}</time>
                    <span>
                      {e.title}
                      {e.actor ? <span className="who"> {e.actor}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          {d.register ? (
            <details className="block register-all">
              <summary>{sv.vehicleCard.sections.registerAll}</summary>
              <RegisterHeadings data={d.register.data} />
            </details>
          ) : null}
          <p className="caption">{vehicleName(d.vehicle)}</p>
        </>
      ) : null}
    </>
  );
}

/** Everything the register returned, under Transportstyrelsen's six headings. */
function RegisterHeadings({ data }: { data: Record<string, unknown> }) {
  const show = (v: unknown) =>
    v === null || v === undefined
      ? sv.none
      : typeof v === 'boolean'
        ? v
          ? sv.yes
          : sv.no
        : String(v);
  const facts = (fields: Record<string, string>) =>
    Object.entries(fields)
      .filter(([k]) => k in data)
      .map(([k, label]) => ({ key: k, label, value: show(data[k]) }));
  const known = new Set(REGISTER_HEADINGS.flatMap((h) => Object.keys(h.fields)));
  const rest = Object.keys(data).filter((k) => !known.has(k));
  return (
    <>
      <h3 className="form-heading">{sv.vehicleCard.sections.registerSummary}</h3>
      <Facts top={false} facts={facts(REGISTER_SUMMARY)} />
      {REGISTER_HEADINGS.map((h) => (
        <div key={h.title}>
          <h3 className="form-heading">{h.title}</h3>
          <Facts top={false} facts={facts(h.fields)} />
        </div>
      ))}
      {rest.length > 0 ? (
        <>
          <h3 className="form-heading">{sv.vehicleCard.sections.registerOther}</h3>
          <Facts
            top={false}
            facts={rest.map((k) => ({ key: k, label: k.replace(/_/g, ' '), value: show(data[k]) }))}
          />
        </>
      ) : null}
    </>
  );
}
